export const tools = [
  {
    id: 'ac2-icon-patcher',
    title: {
      en: "AC2IconPatcher",
      sr: "AC2IconPatcher"
    },
    shortDescription: {
      en: "Reverse-engineered binary archive patcher with a C# port of LZO2A decompression",
      sr: "Patcher binarnih arhiva nastao reverznim inženjeringom, sa C# portom LZO2A dekompresije"
    },
    fullDescription: {
      en: `A command-line tool that reads and writes Ubisoft's undocumented Anvil .forge archive format directly, used to replace Assassin's Creed 2's console-style HUD icons with real keyboard keys. No external tools, no third-party libraries.

The archive format was worked out from the files themselves: the index layout (entries not ordered by offset, name table not at a fixed address), the .data container whose chunks are compressed with LZO2A rather than LZO1X (which every existing managed LZO port implements), and an Adler-32 checksum seeded with 0 instead of 1. Icons are rendered in code at install time, so no copyrighted game assets are redistributed. Ships as a self-contained single-file exe with full backup and restore.`,
      sr: `Alat za komandnu liniju koji direktno čita i piše Ubisoft-ov nedokumentovani Anvil .forge format arhiva, korišćen za zamjenu konzolnih HUD ikonica u Assassin's Creed 2 stvarnim tasterima tastature. Bez eksternih alata i biblioteka trećih strana.

Format arhive je rekonstruisan iz samih fajlova: raspored indeksa (unosi nisu poređani po offsetu, tabela imena nije na fiksnoj adresi), .data kontejner čiji su chunk-ovi kompresovani LZO2A algoritmom umjesto LZO1X (koji implementiraju svi postojeći managed LZO portovi) i Adler-32 checksum sa početnom vrijednošću 0 umjesto 1. Ikonice se iscrtavaju u kodu pri instalaciji, pa se ne distribuiraju zaštićeni resursi igre. Isporučuje se kao self-contained exe sa potpunim backup-om i vraćanjem.`
    },
    technologies: ["C#", ".NET", "Binary Formats", "LZO2A", "Reverse Engineering", "GitHub Actions"],
    highlights: {
      en: [
        "Reverse-engineered an undocumented binary archive format (index, name table, chunk container)",
        "C# port of LZO2A decompression; existing managed LZO ports only cover LZO1X",
        "Non-standard Adler-32 checksum (seed 0) identified and implemented",
        "Icons rendered in code at install time, no copyrighted assets redistributed",
        "Automatic install detection, per-file .bak backups and one-step restore",
        "Self-contained single-file exe, no .NET runtime required",
        "GPL v2+ (inherits from the LZO port)"
      ],
      sr: [
        "Reverzni inženjering nedokumentovanog binarnog formata arhive (indeks, tabela imena, kontejner chunk-ova)",
        "C# port LZO2A dekompresije; postojeći managed LZO portovi pokrivaju samo LZO1X",
        "Identifikovan i implementiran nestandardni Adler-32 checksum (seed 0)",
        "Ikonice se iscrtavaju u kodu pri instalaciji, bez distribucije zaštićenih resursa",
        "Automatsko pronalaženje instalacije, .bak backup po fajlu i vraćanje u jednom koraku",
        "Self-contained exe u jednom fajlu, bez potrebe za .NET runtime-om",
        "GPL v2+ (nasljeđeno od LZO porta)"
      ]
    },
    github: "https://github.com/Bat0oo/AC2IconPatcher"
  },
  {
    id: 'aoh2-diplomacy-fix',
    title: {
      en: "AoH2 Diplomacy Fix & Watcher",
      sr: "AoH2 Diplomacy Fix & Watcher"
    },
    shortDescription: {
      en: "Repairs NaN float corruption in Java-serialized saves, plus a background watcher that prevents it",
      sr: "Popravlja NaN korupciju float vrijednosti u Java-serijalizovanim fajlovima, plus pozadinski watcher koji je sprječava"
    },
    fullDescription: {
      en: `Two tools for a known, unpatched bug in Age of History II where diplomacy values in save files become IEEE 754 NaN, freezing every relation at 0.

The one-time fixer parses the Java Object Serialization stream, locates corrupted floats inside the relation lists, and repairs them, with scan and dry-run modes and a timestamped backup before any write. The watcher prevents the problem instead of only repairing it: it runs in the background (system tray or CLI), snapshots the exact relation values on every clean save, and when corruption appears it backs up and restores the original values from the snapshot rather than a generic default. Both ship as standalone Windows executables and work on Android saves too.`,
      sr: `Dva alata za poznati, nepopravljeni bag u Age of History II gdje diplomatske vrijednosti u sačuvanim fajlovima postaju IEEE 754 NaN, pa su svi odnosi zaključani na 0.

Jednokratni fixer parsira Java Object Serialization stream, pronalazi oštećene float vrijednosti u listama odnosa i popravlja ih, sa scan i dry-run režimima i backup-om sa vremenskom oznakom prije svakog upisa. Watcher problem sprječava umjesto da ga samo popravlja: radi u pozadini (system tray ili CLI), pri svakom ispravnom snimanju pravi snapshot tačnih vrijednosti, a kada se pojavi korupcija pravi backup i vraća originalne vrijednosti iz snapshot-a umjesto generičke podrazumijevane. Oba se isporučuju kao samostalni Windows exe fajlovi i rade i sa Android fajlovima.`
    },
    technologies: ["Python", "Java Serialization", "IEEE 754", "watchdog", "pystray", "PyInstaller"],
    highlights: {
      en: [
        "Fix: detects and repairs IEEE 754 NaN values inside Java-serialized float lists",
        "Fix: scan and dry-run modes, timestamped backup before any write",
        "Watcher: background file monitoring with system tray and CLI modes",
        "Watcher: snapshots clean values and restores the exact originals on corruption",
        "35 automated tests, including stress tests",
        "Standalone Windows executables via PyInstaller; works on Android saves",
        "Full code walkthrough docs for transparency (MIT)"
      ],
      sr: [
        "Fix: pronalazi i popravlja IEEE 754 NaN vrijednosti u Java-serijalizovanim float listama",
        "Fix: scan i dry-run režimi, backup sa vremenskom oznakom prije svakog upisa",
        "Watcher: praćenje fajlova u pozadini, system tray i CLI režim",
        "Watcher: snapshot ispravnih vrijednosti i vraćanje tačnih originala pri korupciji",
        "35 automatizovanih testova, uključujući stres testove",
        "Samostalni Windows exe fajlovi preko PyInstaller-a; radi i sa Android fajlovima",
        "Detaljna dokumentacija koda radi transparentnosti (MIT)"
      ]
    },
    github: "https://github.com/Bat0oo/aoh2-diplomacy-fix"
  },
  {
    id: 'aoh2-save-editor',
    title: {
      en: "AoH2 Save Editor",
      sr: "AoH2 Save Editor"
    },
    shortDescription: {
      en: "Desktop editor for binary Java-serialized saves with a lossless decode-edit-encode pipeline",
      sr: "Desktop editor za binarne Java-serijalizovane fajlove sa decode-edit-encode pipeline-om bez gubitaka"
    },
    fullDescription: {
      en: `A desktop editor that opens Age of History II save files, which are stored as binary Java Object Serialization streams, and presents them as a filterable, editable tree.

The core is a decode-edit-encode pipeline that writes back a byte-valid stream the game accepts, including a workaround for a collection-annotation bug in the javaobj library. The UI uses lazy tree loading so large saves stay responsive, field=value filtering, Batch Edit to change many objects in one action, background threading with progress feedback, and automatic timestamped backups. Releases are built by a GitHub Actions workflow that produces a portable exe on every version tag. A browser version runs the same Python codec via Pyodide/WebAssembly.`,
      sr: `Desktop editor koji otvara sačuvane fajlove igre Age of History II, zapisane kao binarni Java Object Serialization stream-ovi, i prikazuje ih kao stablo koje se može filtrirati i uređivati.

Jezgro je decode-edit-encode pipeline koji vraća bajt-validan stream koji igra prihvata, uključujući zaobilaženje baga sa anotacijama kolekcija u javaobj biblioteci. UI koristi lijeno učitavanje stabla kako bi veliki fajlovi ostali responzivni, filtriranje po field=value, Batch Edit za izmjenu više objekata odjednom, rad u pozadinskim nitima sa prikazom napretka i automatske backup-e sa vremenskom oznakom. Release-ove pravi GitHub Actions workflow koji na svaki version tag generiše portable exe. Verzija za browser pokreće isti Python codec preko Pyodide/WebAssembly.`
    },
    technologies: ["Python", "Tkinter", "Java Serialization", "javaobj", "GitHub Actions", "PyInstaller", "Pyodide"],
    highlights: {
      en: [
        "Lossless decode-edit-encode pipeline for binary Java Object Serialization",
        "Workaround for a collection-annotation bug in javaobj (sync_collections)",
        "Lazy tree loading keeps multi-MB saves responsive",
        "field=value filtering and Batch Edit across many objects",
        "Background threading with progress feedback, automatic timestamped backups",
        "CI: GitHub Actions builds and releases the exe on every version tag",
        "Same codec also runs in the browser via Pyodide/WebAssembly (MIT)"
      ],
      sr: [
        "Decode-edit-encode pipeline bez gubitaka za binarnu Java Object Serialization",
        "Zaobilaženje baga sa anotacijama kolekcija u javaobj (sync_collections)",
        "Lijeno učitavanje stabla održava responzivnost i za fajlove od više MB",
        "Filtriranje po field=value i Batch Edit nad više objekata",
        "Rad u pozadinskim nitima sa prikazom napretka, automatske backup-e",
        "CI: GitHub Actions pravi i objavljuje exe na svaki version tag",
        "Isti codec radi i u browser-u preko Pyodide/WebAssembly (MIT)"
      ]
    },
    github: "https://github.com/Bat0oo/aoh2-save-files-editor",
    // Shown as a secondary "Live Demo" button inside the modal only, not on the card.
    liveDemo: "https://aoh2-saves-editor.pages.dev"
  }
];

export default tools;
